import React, { useEffect, createContext } from "react";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import Header from "./components/Header";
import Home from "./components/Home";
import Buttons from "./components/Buttons";
import Settings from "./components/Settings";
import ConfirmWindow from "./components/ConfirmWindow";
import Container from "@mui/material/Container";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Badge from "@mui/material/Badge";
import ListVersions from "./components/ListVersions";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import LocalOffer from "@mui/icons-material/LocalOffer";
import AccessTime from "@mui/icons-material/AccessTime";
import { Box } from "@mui/material";
import { Card, CardHeader, CardBody } from "reactstrap";

import { styled } from "@mui/material/styles";

import { ESPLoader, Transport } from "esptool-js";
import { serial } from "web-serial-polyfill";
import { Terminal } from "@xterm/xterm";
import "../node_modules/@xterm/xterm/css/xterm.css";
import { connectMQTT, disconnectMQTT, getMQTTClient } from "network/mqttClient";

if (!navigator.serial && navigator.usb) navigator.serial = serial;

import {
  connectESP,
  formatMacAddr,
  sleep,
  loadFiles,
  supported,
} from "./lib/esp";
import { loadSettings, defaultSettings } from "./lib/settings";
import { getDevices, downloadFirmware } from "network/Firebase";
import CryptoJS from "crypto-js";

const TOKEN_URL = process.env.REACT_APP_API_URL_TOKEN_GENERATE;
const BOX_SYNC_URL = process.env.REACT_APP_API_URL_BOX_SYNC;
const BASIC_AUTH = process.env.REACT_APP_BASIC_AUTH_TOKEN;

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const resetTransport = async (transport) => {
  await transport.device.setSignals({
    dataTerminalReady: false,
    requestToSend: true,
  });
  await delay(250);
  await transport.device.setSignals({
    dataTerminalReady: false,
    requestToSend: false,
  });
  await delay(250);
};

// Tải binary từ server
async function getBinary(filePath) {
  // const requestBoday = { filePath };

  try {
    // const data = await downloadBinary(requestBoday);
    // console.log("🚀 ~ getBinary ~ data:", data)

    const data2 = await downloadFirmware(filePath);
    return data2;
  } catch (error) {
    console.error("🚀 ~ getBinary ~ error:", error);
    toast.error(`File not found!!!`, {
      position: "top-center",
      toastId: "download_fail",
      autoClose: 3000,
    });
    return null;
  }
}

const xTerm = new Terminal({ rows: 20 });
xTerm.options = {
  lineHeight: 1,
  fontFamily: "Courier New",
  logLevel: "debug",
};

const StyledBadge = styled(Badge)(({ theme }) => {
  const style = {
    "& .MuiBadge-badge": {
      boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
      "&::after": {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        borderRadius: "50%",
        animation: "ripple 1.2s infinite ease-in-out",
        border: "1px solid currentColor",
        content: '""',
      },
    },
    "@keyframes ripple": {
      "0%": {
        transform: "scale(.8)",
        opacity: 1,
      },
      "100%": {
        transform: "scale(2.4)",
        opacity: 0,
      },
    },
  };
  return style;
});

const stringMD5 = (input) => {
  return CryptoJS.MD5(input).toString(CryptoJS.enc.Hex);
};

const App = () => {
  const [connected, setConnected] = React.useState(false); // Connection status
  const [connecting, setConnecting] = React.useState(false);

  const [settingsOpen, setSettingsOpen] = React.useState(false); // Settings Window
  const [settings, setSettings] = React.useState({ ...defaultSettings }); // Settings
  const [confirmErase, setConfirmErase] = React.useState(false); // Confirm Erase Window
  const [confirmProgram, setConfirmProgram] = React.useState(false); // Confirm Flash Window
  const [flashing, setFlashing] = React.useState(false); // Enable/Disable buttons
  const [branchName, setBranchName] = React.useState(""); // Enable/Disable buttons

  const [loading, setLoading] = React.useState(true);
  const [deviceList, setDeviceList] = React.useState([]);
  const [mqttConnected, setMqttConnected] = React.useState(false);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const devices = await getDevices();
        // console.log("🚀 ~ fetchData ~ data:", devices)
        setDeviceList(devices);
        setLoading(false);
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, []);

  const [espInfo, setEspInfo] = React.useState({
    device: undefined,
    transport: undefined,
    chip: undefined,
    esploader: undefined,
    macAddr: undefined,
  });

  const [binaryUpload, setbinaryUpload] = React.useState({
    files: [],
    boardType: "",
  }); // Uploaded Files

  const [authToken, setAuthToken] = React.useState({
    token: null,
    expiresAt: 0,
  });
  const [boxInfo, setBoxInfo] = React.useState({
    boxId: null,
    qrCertificate: null,
    terminalCode: null,
    serialCode: null,
  });

  const getAccessToken = async () => {
    const now = Date.now();
    if (authToken.token && now < authToken.expiresAt - 5000) {
      return authToken.token;
    }

    const response = await fetch(TOKEN_URL, {
      method: "POST",
      headers: {
        Authorization: `Basic ${BASIC_AUTH}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Token request failed: ${response.status}`);
    }

    const data = await response.json();
    const expiresAt = Date.now() + (data.expires_in || 300) * 1000;
    setAuthToken({ token: data.access_token, expiresAt });
    return data.access_token;
  };

  const normalizeMacForSerial = (macAddress) => {
    return macAddress.replace(/[:.]/g, "").toUpperCase();
  };

  const formatMacWithColons = (macAddress) => {
    const upperMac = macAddress.toUpperCase();
    const rawMac = normalizeMacForSerial(upperMac);
    const pairs = rawMac.match(/.{1,2}/g);
    return pairs ? pairs.join(":") : upperMac;
  };

  const generateSerialNumber = (macAddress) => {
    const rawMac = normalizeMacForSerial(macAddress);
    const macValue = BigInt("0x" + rawMac);
    const low = macValue & BigInt("0xFFFFFFFF");
    const high = (macValue >> BigInt(32)) & BigInt("0xFFFFFFFF");
    return `PAYMENT-BOX-${high.toString(10)}-${low.toString(10)}`;
  };

  const fetchBoxInfo = async (macAddress) => {
    const token = await getAccessToken();
    const formattedMac = formatMacWithColons(macAddress);
    const rawMac = normalizeMacForSerial(macAddress);
    const checkSum = stringMD5(`${rawMac}VietQRBoxAccessKey`);
    const serialNumber = generateSerialNumber(macAddress);

    const response = await fetch(BOX_SYNC_URL, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        serialNumber,
        macAddress: formattedMac,
        firmwareVersion: "unknown",
        checkSum,
      }),
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`Box sync request failed: ${response.status} ${errText}`);
    }

    const responseJson = await response.json();
    if (
      responseJson.status !== "SUCCESS" &&
      responseJson.status !== "EXISTED"
    ) {
      throw new Error(
        `Box sync failed: ${responseJson.status} ${responseJson.code || ""} ${
          responseJson.message || ""
        }`
      );
    }

    return responseJson.data || responseJson;
  };

  useEffect(() => {
    setSettings(loadSettings());
  }, []);

  useEffect(() => {
    if (branchName === "") {
      setbinaryUpload({ files: [], boardType: "" });
    }
  }, [branchName]);

  React.useEffect(() => {
    if (!supported()) return;
    const terminal = document.getElementById("terminal");
    xTerm.open(terminal);
  }, []);

  const espLoaderTerminal = {
    clean() {
      xTerm.clear();
    },
    writeLine(data) {
      xTerm.writeln(data);
    },
    write(data) {
      xTerm.write(data);
    },
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!", { autoClose: 2000 });
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  const handleBtnSelectEspVersion = (key) => {
    const url = location.pathname + key;
    setBranchName(key);
  };

  // Connect to ESP & init flasher stuff

  if (navigator.serial != null) {
    navigator.serial.addEventListener("disconnect", async (event) => {
      console.log("🚀 ~ navigator.serial.addEventListener ~ disconnect:");
      if (espInfo.transport) {
        setEspInfo({
          device: undefined,
          transport: undefined,
          chip: undefined,
          esploader: undefined,
        });
        setConnected(false);
        setConnecting(false);
        xTerm.clear();
      }
    });
  }

  const clickConnect = async () => {
    let newDevice, newTransport, newChip, newEspLoader;

    try {
      if (!espInfo.device) {
        newDevice = await navigator.serial.requestPort({});
        newTransport = new Transport(newDevice, true);
      }
      const flashOptions = {
        transport: newTransport,
        baudrate: parseInt(settings.baudRate),
        terminal: espLoaderTerminal,
        enableTracing: false,
      };
      setConnecting(true);
      toast.info("Connecting...", {
        position: "top-center",
        autoClose: false,
        toastId: "connecting",
      });
      toast.update("connecting", {
        render: "Connecting...",
        type: toast.TYPE.INFO,
        autoClose: false,
      });
      newEspLoader = new ESPLoader(flashOptions);

      newChip = await newEspLoader.main();
      console.log("🚀 ~ clickConnect ~ newChip:", newChip);

      // Đọc địa chỉ MAC từ chip
      const macAddr = await newEspLoader.chip.readMac(newEspLoader);

      setEspInfo({
        device: newDevice,
        transport: newTransport,
        chip: newChip,
        esploader: newEspLoader,
        macAddr, // Lưu địa chỉ MAC vào state
      });

      setConnected(true);
      toast.update("connecting", {
        render: `Connected 🚀\nMAC: ${macAddr}`,
        type: toast.TYPE.SUCCESS,
        autoClose: 3000,
      });

      try {
        const boxData = await fetchBoxInfo(macAddr);
        console.log("🚀 ~ clickConnect ~ boxData:", boxData);
        setBoxInfo({
          boxId: boxData?.boxId,
          qrCertificate: boxData?.certificate,
          terminalCode: boxData?.qrBoxCode,
          serialCode: boxData?.serialNumber,
        });
        // console.log("qrCertificate:", boxData?.certificate);
        // console.log("qrBoxCode:", boxData?.qrBoxCode);
        // xTerm.writeln(`qrCertificate: ${boxData.certificate}`);
        // xTerm.writeln(`qrBoxCode: ${boxData.qrBoxCode}`);
      } catch (boxError) {
        console.error("Failed to fetch box info:", boxError);
      }

      console.log(`MAC Address: ${macAddr}`);
    } catch (e) {
      console.error(e);
    }
  };

  const programOneBinary = async () => {
    xTerm.clear();
    setConfirmProgram(false);
    setFlashing(true);

    //  let success = false;

    if (!binaryUpload.files || binaryUpload.files.length === 0) {
      toast.error(`Must select version image ...`, {
        position: "top-center",
        toastId: "upload_fail",
        autoClose: 3000,
      });
      setFlashing(false);
      return;
    }

    try {
      // Tạo mảng fileArray cho writeFlash
      const fileArray = [];
      for (const file of binaryUpload.files) {
        toast.info(`Đang tải file ${file.name}...`, {
          toastId: "download_file",
          position: "top-center",
        });
        const fileInfo = await getBinary(file.filePath);
        if (!fileInfo) {
          await delay(2000);
          disconnectButtonOnclick();
          toast.dismiss("download_file");
          return;
        }

        const contents = fileInfo;
        fileArray.push({
          data: contents,
          address: parseInt(file.offset, 16),
        });
        toast.dismiss("download_file");
      }

      toast(`Uploading...`, {
        position: "top-center",
        progress: 0,
        toastId: "upload",
      });

      const flashOptions = {
        fileArray,
        flashSize: "keep",
        flashMode: "keep",
        flashFreq: "keep",
        eraseAll: false,
        compress: true,
        baudrate: 921600,
        reportProgress: (fileIndex, written, total) => {
          const progress = written / total;
          toast.update("upload", { progress: progress });
          if (progress >= 1) {
            toast.dismiss("upload"); // Đóng toast "upload"
            toast.success("Done! Reset ESP to run new firmware.", {
              position: "top-center",
              toastId: "uploaded",
              autoClose: 3000,
            });
          }
        },
      };

      await espInfo.esploader.writeFlash(flashOptions);

      // Thêm logic reset ESP sau khi flash xong
      const transport = espInfo.transport;
      if (transport && transport.device) {
        await transport.device.setSignals({
          dataTerminalReady: false,
          requestToSend: true,
        });
        await delay(100);
        await transport.device.setSignals({
          dataTerminalReady: true,
          requestToSend: false,
        });
        await delay(100);
        await transport.device.setSignals({
          dataTerminalReady: false,
          requestToSend: false,
        });
        toast.info("ESP has been reset successfully.", {
          position: "top-center",
          autoClose: 3000,
        });
      }
      await delay(2000);
      disconnectButtonOnclick();
    } catch (e) {
      console.error(e);
      const currTransport = espInfo.transport;
      await resetTransport(currTransport);
      await currTransport.disconnect();
    }
    setFlashing(false);
  };

  const eraseButtonOnclick = async () => {
    setConfirmErase(false);
    setFlashing(true);
    try {
      toast.warning(`Erasing flash memory. Please wait...`, {
        position: "top-center",
        toastId: "erase_flash",
        autoClose: false,
      });
      await espInfo.esploader.eraseFlash();
      toast.update("erase_flash", {
        render: "Finished erasing memory.",
        type: toast.TYPE.SUCCESS,
        autoClose: 2000,
      });

      await delay(1000);
      // disconnectButtonOnclick();
    } catch (e) {
      console.error(e);
      toast.update("erase", {
        render: `ERROR!\n${e}`,
        type: toast.TYPE.ERROR,
        autoClose: 3000,
      });
    } finally {
      setFlashing(false);
    }
  };

  const disconnectButtonOnclick = async () => {
    if (espInfo.transport) {
      const currTransport = espInfo.transport;
      await resetTransport(currTransport);
      await currTransport.disconnect();

      setEspInfo({
        device: undefined,
        transport: undefined,
        chip: undefined,
        esploader: undefined,
      });
      setConnected(false);
      setConnecting(false);
      xTerm.clear();
    }
  };
  const renderListDevice = (input) => {
    const formatDate = (ts) => {
      if (!ts) return "";
      // Firestore timestamp dạng { seconds, nanoseconds }
      if (ts.seconds)
        return new Date(ts.seconds * 1000).toLocaleString("vi-VN", {
          hour: "2-digit",
          minute: "2-digit",
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      // nếu là Date object hoặc millisecond number
      if (ts.toDate) return ts.toDate().toLocaleString("vi-VN");
      if (typeof ts === "number") return new Date(ts).toLocaleString("vi-VN");
      return new Date(ts).toLocaleString("vi-VN");
    };

    return (
      <Stack spacing={2}>
        {input.map((device, idx) => {
          const connected = !!device.isConnected;
          return (
            <Button
              key={idx}
              onClick={() => handleBtnSelectEspVersion(device.gitBranch)}
              variant="outlined"
              fullWidth
              aria-label={`device-${idx}`}
              sx={{
                justifyContent: "flex-start",
                textTransform: "none",
                p: 1,
                borderWidth: 2, // bolder border
                borderColor: connected ? "success.main" : "grey.300",
                borderRadius: 2,
                alignItems: "center",
                display: "flex",
                "&:hover": { boxShadow: 4, borderColor: "primary.main" },
              }}
            >
              {/* Accent bar để dễ phân biệt */}
              <Box
                sx={{
                  width: 8,
                  height: 56,
                  borderRadius: 1,
                  mr: 1.5,
                  ml: 1,
                  bgcolor: connected ? "success.main" : "grey.400",
                }}
              />

              {/* Avatar */}
              <Avatar
                alt={device.deviceName}
                src={device.srcImage}
                sx={{ width: 56, height: 56, mr: 2 }}
              />

              {/* Nội dung chính */}
              <Box sx={{ flex: 1, textAlign: "left" }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 800, color: "primary.main" }}
                >
                  {device.deviceName}
                  <Typography
                    component="span"
                    sx={{ fontWeight: 500, ml: 1, color: "text.secondary" }}
                  >
                    ({device.chipType?.toUpperCase() || "N/A"})
                  </Typography>
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mt: 0.25 }}
                >
                  GIT BRANCH: {device.gitBranch}
                </Typography>

                <Box
                  sx={{
                    mt: 0.5,
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  {/* Latest version: nổi bật như badge */}
                  {device.latestVersion && (
                    <Chip
                      icon={<LocalOffer />}
                      label={device.latestVersion}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        bgcolor: "primary.main",
                        color: "#fff",
                        height: 28,
                        "& .MuiChip-icon": { color: "#fff" },
                      }}
                    />
                  )}

                  {/* UpdatedAt: nhỏ, nhẹ nhàng phía sau */}
                  {device.updatedAt && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <AccessTime fontSize="small" sx={{ mr: 0.5 }} />
                      Cập nhật: {formatDate(device.updatedAt)}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Optional: small status dot (ở cuối) */}
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  ml: 2,
                  bgcolor: connected ? "success.main" : "error.main",
                }}
              />
            </Button>
          );
        })}
      </Stack>
    );
  };

  // Kết nối MQTT khi đăng nhập thành công
  // React.useEffect(() => {
  //   const client = connectMQTT();

  //   if (client) {
  //     client.on("connect", () => {
  //       setMqttConnected(true);
  //       toast.success("MQTT Client Connected", {
  //         position: "top-center",
  //         autoClose: 3000,
  //       });
  //     });

  //     client.on("close", () => {
  //       setMqttConnected(false);
  //       toast.error("MQTT Client Disconnected", {
  //         position: "top-center",
  //         autoClose: 3000,
  //       });
  //     });
  //   }

  //   // Cleanup khi component bị unmount
  //   return () => {
  //     disconnectMQTT();
  //   };
  // }, []);

  const sendPayloadToMQTT = async () => {
    const client = getMQTTClient();

    if (!client || !mqttConnected) {
      toast.error("MQTT Client is not connected.", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    if (!espInfo.macAddr) {
      toast.error("MAC Address not found. Please connect to ESP first.", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    const macAddr = espInfo.macAddr;
    const accessKey = "VietQRBoxAccessKey"; // Key cố định
    const checkSum = stringMD5(macAddr + accessKey);

    const payload = {
      macAddr,
      checkSum,
      firmwareVersion: "unknown",
    };

    console.log("Payload to MQTT:", payload);

    // Gửi payload lên MQTT Broker
    try {
      client.publish("your/topic", JSON.stringify(payload));
      toast.success("Payload sent to MQTT successfully!", {
        position: "top-center",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Failed to send payload to MQTT:", error);
      toast.error("Failed to send payload to MQTT.", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* Toaster */}
      <ToastContainer />
      <Header sx={{ mb: "1rem" }} />
      <Grid sx={{ flexGrow: 1 }} container spacing={2}>
        <Grid item xs={4}>
          {supported() && connected && <></>}

          {supported() && branchName != "" ? (
            <ListVersions
              setbinaryUpload={setbinaryUpload}
              binaryUpload={binaryUpload}
              branchName={branchName}
              setBranchName={setBranchName}
            ></ListVersions>
          ) : (
            <Card className="shadow">
              <CardHeader className="bg-transparent">
                <Typography
                  variant="h5"
                  component="h5"
                  sx={{ color: "black", paddingLeft: "5px" }}
                >
                  Các phiên bản sản phẩm
                </Typography>
              </CardHeader>
              <CardBody>
                {loading ? <CircularProgress /> : renderListDevice(deviceList)}
              </CardBody>
            </Card>
          )}
        </Grid>

        <Grid item xs={8}>
          <div
            style={{
              display: "flex",
              padding: "0 24px 0 24px",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <div style={{ flex: 1, width: "100%" }}>
              {!connected && !connecting && (
                <Home
                  connect={clickConnect}
                  supported={supported}
                  openSettings={() => setSettingsOpen(true)}
                />
              )}
              {!connected && connecting && (
                <Grid item p="0 24px 0 24px">
                  <Typography
                    variant="h3"
                    component="h2"
                    sx={{ color: "#aaa" }}
                  >
                    Connecting...
                  </Typography>
                </Grid>
              )}

              {/* Erase & Program Buttons */}
              {connected && (
                <Grid item p="0 24px 0 24px">
                  <Buttons
                    erase={() => setConfirmErase(true)}
                    program={() => setConfirmProgram(true)}
                    disconnect={disconnectButtonOnclick}
                    disabled={flashing}
                  />
                </Grid>
              )}

              {/* Box Information */}
              {connected && boxInfo.boxId && (
                <Grid item p="0 24px 0 24px">
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    Thông tin định danh thiết bị
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{ mb: 1 }}
                    >
                      <Typography variant="body1">
                        QR Certificate: Click to copy
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => copyToClipboard(boxInfo.qrCertificate)}
                      >
                        Copy
                      </Button>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body1">
                        Terminal Code: {boxInfo.terminalCode}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => copyToClipboard(boxInfo.terminalCode)}
                      >
                        Copy
                      </Button>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body1">
                        Serial Code: {boxInfo.serialCode}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => copyToClipboard(boxInfo.serialCode)}
                      >
                        Copy
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              )}
            </div>
            <div style={{ flex: 1, width: "100%" }}>
              {/* Serial Output */}
              {supported() && (
                <div id="terminal" style={{ marginTop: "10px" }}></div>
              )}
            </div>
          </div>
        </Grid>
      </Grid>

      <Container maxWidth="sm">
        {/* Settings Window */}
        <Settings
          open={settingsOpen}
          close={() => setSettingsOpen(false)}
          setSettings={setSettings}
          settings={settings}
          connected={connected}
        />
      </Container>

      {/* Confirm Erase Window */}
      <ConfirmWindow
        open={confirmErase}
        text={"This will erase the memory of your ESP."}
        onOk={eraseButtonOnclick}
        onCancel={() => setConfirmErase(false)}
      />

      {/* Confirm Flash/Program Window */}
      <ConfirmWindow
        open={confirmProgram}
        text={"Flashing new firmware will override the current firmware."}
        onOk={programOneBinary}
        onCancel={() => setConfirmProgram(false)}
      />
    </div>
  );
};

export default App;
